// ==============================================
// DNACORE 2.0 - MÓDULO ACADÊMICO
// Hub unificado de gestão acadêmica
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { ModuleHeader } from '@/design-system/dna';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  GraduationCap,
  Users,
  UserCircle,
  BookOpen,
  FileText,
  Calendar,
  BarChart3,
  ClipboardList,
  Search,
  Filter,
  Plus,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  Edit,
  MoreHorizontal,
  Download,
  Printer,
  Target,
  Award,
  Percent
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';

// ================== COMPONENTES AUXILIARES ==================

// Card de Estatística
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'violet', trend, onClick }) => {
  const colorClasses = {
    violet: 'from-violet-500 to-purple-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber: 'from-amber-500 to-orange-600',
    rose: 'from-rose-500 to-pink-600',
    blue: 'from-blue-500 to-indigo-600'
  };

  return (
    <Card className="cursor-pointer hover:shadow-lg transition-all" onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">{title}</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{value}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            {trend && (
              <div className={cn(
                "flex items-center gap-1 mt-2 text-sm font-medium",
                trend.type === 'up' ? 'text-emerald-600' : 'text-rose-600'
              )}>
                {trend.type === 'up' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span>{trend.value}</span>
              </div>
            )}
          </div>
          <div className={cn("p-3 rounded-xl bg-gradient-to-br", colorClasses[color])}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Turma
const TurmaCard = ({ turma, onView, onEdit }) => {
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold text-slate-800">{turma.name}</h4>
              <Badge variant="outline" className="text-xs">
                {turma.turno || 'Manhã'}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Alunos</p>
                <p className="font-medium text-slate-700">{turma.total_alunos || 0}</p>
              </div>
              <div>
                <p className="text-slate-500">Média</p>
                <p className={cn(
                  "font-medium",
                  (turma.media_geral || 0) >= 7 ? "text-emerald-600" : "text-amber-600"
                )}>
                  {turma.media_geral?.toFixed(1) || '-'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Frequência</p>
                <p className="font-medium text-slate-700">{turma.frequencia_media || '-'}%</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={() => onView?.(turma)}>
              <Eye className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => onEdit?.(turma)}>
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Professor
const ProfessorCard = ({ professor, onView }) => {
  return (
    <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => onView?.(professor)}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold">
            {professor.name?.charAt(0) || 'P'}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-800 truncate">{professor.name}</h4>
            <p className="text-sm text-slate-500 truncate">
              {professor.disciplinas?.length || 0} disciplina(s) • {professor.turmas?.length || 0} turma(s)
            </p>
          </div>
          <div className="text-right">
            {professor.pendencias > 0 ? (
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                {professor.pendencias} pendência(s)
              </Badge>
            ) : (
              <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                Em dia
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Disciplina
const DisciplinaCard = ({ disciplina, onView }) => {
  return (
    <Card className="hover:shadow-md transition-all cursor-pointer" onClick={() => onView?.(disciplina)}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center",
            "bg-gradient-to-br from-blue-500 to-indigo-600"
          )}>
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-800 truncate">{disciplina.name}</h4>
            <p className="text-sm text-slate-500">
              {disciplina.carga_horaria || '-'} h/semana
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Média geral</p>
            <p className={cn(
              "font-bold text-lg",
              (disciplina.media_geral || 0) >= 7 ? "text-emerald-600" : "text-amber-600"
            )}>
              {disciplina.media_geral?.toFixed(1) || '-'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Tabela de Notas
const TabelaNotas = ({ notas, onEdit }) => {
  if (!notas || notas.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Nenhuma nota lançada</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 font-medium text-slate-600">Aluno</th>
            <th className="text-center py-3 px-4 font-medium text-slate-600">1º Tri</th>
            <th className="text-center py-3 px-4 font-medium text-slate-600">2º Tri</th>
            <th className="text-center py-3 px-4 font-medium text-slate-600">3º Tri</th>
            <th className="text-center py-3 px-4 font-medium text-slate-600">Média</th>
            <th className="text-center py-3 px-4 font-medium text-slate-600">Status</th>
          </tr>
        </thead>
        <tbody>
          {notas.map((aluno, idx) => {
            const media = ((aluno.tri1 || 0) + (aluno.tri2 || 0) + (aluno.tri3 || 0)) / 3;
            const aprovado = media >= 7;
            
            return (
              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4">
                  <p className="font-medium text-slate-700">{aluno.nome}</p>
                </td>
                <td className="text-center py-3 px-4">
                  <span className={cn(
                    "font-medium",
                    (aluno.tri1 || 0) >= 7 ? "text-emerald-600" : (aluno.tri1 || 0) >= 5 ? "text-amber-600" : "text-rose-600"
                  )}>
                    {aluno.tri1?.toFixed(1) || '-'}
                  </span>
                </td>
                <td className="text-center py-3 px-4">
                  <span className={cn(
                    "font-medium",
                    (aluno.tri2 || 0) >= 7 ? "text-emerald-600" : (aluno.tri2 || 0) >= 5 ? "text-amber-600" : "text-rose-600"
                  )}>
                    {aluno.tri2?.toFixed(1) || '-'}
                  </span>
                </td>
                <td className="text-center py-3 px-4">
                  <span className={cn(
                    "font-medium",
                    (aluno.tri3 || 0) >= 7 ? "text-emerald-600" : (aluno.tri3 || 0) >= 5 ? "text-amber-600" : "text-rose-600"
                  )}>
                    {aluno.tri3?.toFixed(1) || '-'}
                  </span>
                </td>
                <td className="text-center py-3 px-4">
                  <span className={cn(
                    "font-bold",
                    media >= 7 ? "text-emerald-600" : media >= 5 ? "text-amber-600" : "text-rose-600"
                  )}>
                    {media.toFixed(1)}
                  </span>
                </td>
                <td className="text-center py-3 px-4">
                  <Badge className={cn(
                    aprovado ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                  )}>
                    {aprovado ? 'Aprovado' : 'Recuperação'}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ================== COMPONENTE PRINCIPAL ==================

export default function Academico() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('visao-geral');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dados
  const [turmas, setTurmas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    totalTurmas: 0,
    totalAlunos: 0,
    totalProfessores: 0,
    totalDisciplinas: 0,
    mediaGeral: 0,
    frequenciaMedia: 0,
    notasPendentes: 0,
    fechamentoPendente: 0
  });

  // Carregar dados
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Carregar turmas
      const turmasRes = await fetch(`${API_URL}/api/class-groups`).catch(() => null);
      const turmasData = turmasRes ? await turmasRes.json() : [];
      setTurmas(Array.isArray(turmasData) ? turmasData : []);

      // Carregar professores
      const professoresRes = await fetch(`${API_URL}/api/employees?role=professor`).catch(() => null);
      const professoresData = professoresRes ? await professoresRes.json() : [];
      setProfessores(Array.isArray(professoresData) ? professoresData : []);

      // Carregar disciplinas (courses)
      const disciplinasRes = await fetch(`${API_URL}/api/courses`).catch(() => null);
      const disciplinasData = disciplinasRes ? await disciplinasRes.json() : [];
      setDisciplinas(Array.isArray(disciplinasData) ? disciplinasData : []);

      // Calcular estatísticas
      const turmasArray = Array.isArray(turmasData) ? turmasData : [];
      const professoresArray = Array.isArray(professoresData) ? professoresData : [];
      const disciplinasArray = Array.isArray(disciplinasData) ? disciplinasData : [];

      setEstatisticas({
        totalTurmas: turmasArray.length,
        totalAlunos: turmasArray.reduce((sum, t) => sum + (t.total_alunos || 0), 0),
        totalProfessores: professoresArray.length,
        totalDisciplinas: disciplinasArray.length,
        mediaGeral: 7.8, // Placeholder
        frequenciaMedia: 92, // Placeholder
        notasPendentes: 15, // Placeholder
        fechamentoPendente: 3 // Placeholder
      });

    } catch (error) {
      console.error('Erro ao carregar dados acadêmicos:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrar dados
  const turmasFiltradas = turmas.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const professoresFiltrados = professores.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const disciplinasFiltradas = disciplinas.filter(d => 
    d.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <ModuleHeader kicker="Acadêmico" title="Acadêmico" subtitle="Gestão de turmas, professores, notas e frequência" icon={GraduationCap} testid="academico-header"
        actions={<>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Atualizar
          </Button>
          <Button className="dna-mh-primary">
            <Plus className="h-4 w-4 mr-2" />
            Nova Turma
          </Button>
        </>} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Turmas"
          value={estatisticas.totalTurmas}
          subtitle={`${estatisticas.totalAlunos} alunos`}
          icon={Users}
          color="violet"
          onClick={() => setActiveTab('turmas')}
        />
        <StatCard
          title="Professores"
          value={estatisticas.totalProfessores}
          subtitle={`${estatisticas.totalDisciplinas} disciplinas`}
          icon={UserCircle}
          color="blue"
          onClick={() => setActiveTab('professores')}
        />
        <StatCard
          title="Média Geral"
          value={estatisticas.mediaGeral.toFixed(1)}
          subtitle="Todas as turmas"
          icon={Award}
          color="emerald"
          trend={{ type: 'up', value: '+0.3 vs trimestre anterior' }}
        />
        <StatCard
          title="Frequência"
          value={`${estatisticas.frequenciaMedia}%`}
          subtitle="Média geral"
          icon={Calendar}
          color="amber"
        />
      </div>

      {/* Alertas */}
      {(estatisticas.notasPendentes > 0 || estatisticas.fechamentoPendente > 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <AlertCircle className="h-6 w-6 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Atenção necessária</p>
                <p className="text-sm text-amber-700">
                  {estatisticas.notasPendentes > 0 && `${estatisticas.notasPendentes} notas pendentes de lançamento. `}
                  {estatisticas.fechamentoPendente > 0 && `${estatisticas.fechamentoPendente} turmas aguardando fechamento.`}
                </p>
              </div>
              <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                Ver pendências
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="visao-geral" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="turmas" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Turmas
            </TabsTrigger>
            <TabsTrigger value="professores" className="flex items-center gap-2">
              <UserCircle className="h-4 w-4" />
              Professores
            </TabsTrigger>
            <TabsTrigger value="disciplinas" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Disciplinas
            </TabsTrigger>
            <TabsTrigger value="notas" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notas
            </TabsTrigger>
            <TabsTrigger value="frequencia" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Frequência
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-2">
            <Input
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
          </div>
        </div>

        {/* Tab: Visão Geral */}
        <TabsContent value="visao-geral" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Turmas com Atenção */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Turmas com Atenção</CardTitle>
                <CardDescription>Turmas com média abaixo de 7 ou frequência baixa</CardDescription>
              </CardHeader>
              <CardContent>
                {turmas.slice(0, 5).map((turma, idx) => (
                  <div key={idx} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-slate-700">{turma.name}</p>
                      <p className="text-sm text-slate-500">{turma.total_alunos || 0} alunos</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-amber-600">Média: 6.5</p>
                      <p className="text-sm text-slate-500">Freq: 85%</p>
                    </div>
                  </div>
                ))}
                {turmas.length === 0 && (
                  <p className="text-center py-6 text-slate-500">Nenhuma turma cadastrada</p>
                )}
              </CardContent>
            </Card>

            {/* Professores com Pendências */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Professores com Pendências</CardTitle>
                <CardDescription>Lançamentos pendentes de notas ou frequência</CardDescription>
              </CardHeader>
              <CardContent>
                {professores.slice(0, 5).map((prof, idx) => (
                  <div key={idx} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-violet-600">{prof.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">{prof.name}</p>
                        <p className="text-sm text-slate-500">2 disciplinas</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-amber-300 text-amber-700">
                      3 pendências
                    </Badge>
                  </div>
                ))}
                {professores.length === 0 && (
                  <p className="text-center py-6 text-slate-500">Nenhum professor cadastrado</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Fechamento do Trimestre */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-5 w-5 text-violet-600" />
                Checklist de Fechamento - 1º Trimestre
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-emerald-700">Notas Lançadas</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">78%</p>
                  <p className="text-sm text-emerald-600">312 de 400 lançamentos</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="font-medium text-amber-700">Frequência Registrada</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-700">92%</p>
                  <p className="text-sm text-amber-600">Faltam 8 dias para registrar</p>
                </div>
                <div className="p-4 bg-violet-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-violet-600" />
                    <span className="font-medium text-violet-700">Boletins Gerados</span>
                  </div>
                  <p className="text-2xl font-bold text-violet-700">0%</p>
                  <p className="text-sm text-violet-600">Aguardando fechamento</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Turmas */}
        <TabsContent value="turmas" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {turmasFiltradas.map((turma) => (
              <TurmaCard
                key={turma.id}
                turma={turma}
                onView={(t) => navigate(`/v2/academico/turmas/${t.id}`)}
                onEdit={(t) => toast.info(`Editar turma: ${t.name}`)}
              />
            ))}
          </div>
          {turmasFiltradas.length === 0 && (
            <Card className="py-12">
              <CardContent className="text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhuma turma encontrada</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab: Professores */}
        <TabsContent value="professores" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {professoresFiltrados.map((professor) => (
              <ProfessorCard
                key={professor.id}
                professor={professor}
                onView={(p) => navigate(`/v2/academico/professores/${p.id}`)}
              />
            ))}
          </div>
          {professoresFiltrados.length === 0 && (
            <Card className="py-12">
              <CardContent className="text-center">
                <UserCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhum professor encontrado</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab: Disciplinas */}
        <TabsContent value="disciplinas" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {disciplinasFiltradas.map((disciplina) => (
              <DisciplinaCard
                key={disciplina.id}
                disciplina={disciplina}
                onView={(d) => navigate(`/v2/academico/disciplinas/${d.id}`)}
              />
            ))}
          </div>
          {disciplinasFiltradas.length === 0 && (
            <Card className="py-12">
              <CardContent className="text-center">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhuma disciplina encontrada</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab: Notas */}
        <TabsContent value="notas" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Lançamento de Notas</CardTitle>
                  <CardDescription>Selecione a turma e disciplina para lançar notas</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select defaultValue="">
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Selecione a turma" />
                    </SelectTrigger>
                    <SelectContent>
                      {turmas.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select defaultValue="">
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Selecione a disciplina" />
                    </SelectTrigger>
                    <SelectContent>
                      {disciplinas.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <TabelaNotas 
                notas={[
                  { nome: 'Ana Silva', tri1: 8.5, tri2: 7.8, tri3: 9.0 },
                  { nome: 'Bruno Santos', tri1: 6.5, tri2: 7.0, tri3: 6.8 },
                  { nome: 'Carla Oliveira', tri1: 9.2, tri2: 8.8, tri3: 9.5 },
                  { nome: 'Daniel Costa', tri1: 5.5, tri2: 6.2, tri3: 5.8 },
                  { nome: 'Elena Martins', tri1: 7.8, tri2: 8.0, tri3: 7.5 }
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Frequência */}
        <TabsContent value="frequencia" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Registro de Frequência</CardTitle>
                  <CardDescription>Selecione a turma e data para registrar frequência</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select defaultValue="">
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Selecione a turma" />
                    </SelectTrigger>
                    <SelectContent>
                      {turmas.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="date" className="w-40" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Selecione uma turma e data para registrar frequência</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
